import { useRef, useState, type ReactNode } from "react"
import { useQuery, useMutation } from "@tanstack/react-query"
import { apiRequest, queryClient } from "@/lib/queryClient"
import { useToast } from "@/hooks/use-toast"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog"
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import type { PillarPost, ContentUpload } from "@shared/schema"
import { isSignalRoomSourced } from "@shared/signalRoom"
import { formatDateTime } from "@/lib/dates"
import { cn } from "@/lib/utils"
import { CalendarClock, CheckCircle, Loader2, Pencil, Trash2, Send, AlertCircle, ArrowLeft, Image as ImageIcon, Images, Clapperboard, ExternalLink } from "lucide-react"
import { FaLinkedin } from "react-icons/fa"
import { useLocation } from "wouter"
import { useEdition } from "@/lib/edition"
import { PostSlides, hasSlides } from "@/components/PostSlides"
import { postModuleHref } from "@shared/postRoutes"
import { MEDIA_FORMAT_LABELS, effectiveMediaFormat, type MediaFormat } from "@shared/pillarFormats"
import { swapBlockedReason } from "@shared/imageSwap"

// Hotel-Edition B1 (PLAN §7.3): type icon per post and a click into the post's module.
const MEDIA_ICON: Record<MediaFormat, typeof ImageIcon> = {
  foto: ImageIcon, karussell: Images, reel: Clapperboard, linkedin: FaLinkedin as unknown as typeof ImageIcon,
}

const QUERY_KEY = ["/api/dfy/weekly-plan"] as const
const WEEKS_KEY = ["/api/dfy/weeks"] as const

// Per-post editability from the server (plan 2026-10-07): approved posts stay editable until they are
// handed over to the publisher.
type EditReason = "plan_review" | "queued" | "in_progress" | "handed_over" | "closed" | "reel_manual"
type PlanPost = PillarPost & { editability?: { canEdit: boolean; canDelete: boolean; reason: EditReason } }

interface WeeklyPlanResponse {
  cycle: { id: string; weekIso: string; phase: string; revision: number } | null
  batch: { id: string; status: string } | null
  posts: PlanPost[]
  allowedActions: { canApprove: boolean; canEdit: boolean; canDelete: boolean }
  publishHeld?: boolean
  /** Hotel owner may swap pictures for photos of the own library (PATCH media, PLAN.md 32g). */
  imageSwapEnabled?: boolean
}

/** Pictures of a post in slide order: the carousel slides, else the single picture. */
function postPictures(p: PillarPost): string[] {
  if (Array.isArray(p.imageUrls) && p.imageUrls.length > 1) return p.imageUrls
  return p.imageUrl ? [p.imageUrl] : []
}

const LOCK_HINT: Partial<Record<EditReason, string>> = {
  in_progress: "Wird gerade zur Veröffentlichung übergeben.",
  handed_over: "Zur Veröffentlichung übergeben, Änderungen sind nicht mehr möglich.",
}

interface WeekSummary { weekIso: string; cycleId: string; phase: string; status: string }
interface WeeksResponse { weeks: WeekSummary[]; currentWeekIso: string }

const PHASE_LABEL: Record<string, { label: string; className: string }> = {
  generating: { label: "Wird erstellt", className: "text-amber-600 border-amber-300" },
  pending_approval: { label: "Bereit zur Freigabe", className: "text-blue-600 border-blue-300" },
  approved: { label: "Freigegeben", className: "text-green-600 border-green-300" },
  publishing: { label: "Wird veröffentlicht", className: "text-green-600 border-green-300" },
  published: { label: "Veröffentlicht", className: "text-green-700 border-green-400" },
  empty: { label: "Kein Plan", className: "text-muted-foreground border-muted" },
  failed: { label: "Fehlgeschlagen", className: "text-red-600 border-red-300" },
  stalled: { label: "Fehler, Ihr Team ist informiert", className: "text-red-600 border-red-300" },
}

export default function Wochenplan() {
  const { toast } = useToast()
  const { edition } = useEdition()
  const isHotel = edition?.kind === "hotel"
  const [, setLocation] = useLocation()
  const [editing, setEditing] = useState<PlanPost | null>(null)
  const [draftTitle, setDraftTitle] = useState("")
  const [draftContent, setDraftContent] = useState("")
  // Picture swap: the slide (1-based) whose photo picker is open, null = closed.
  const [pickerSlide, setPickerSlide] = useState<number | null>(null)
  // Id of the post open in the editor, read by late mutation callbacks (their closure may be stale).
  const editingIdRef = useRef<string | null>(null)
  editingIdRef.current = editing?.id ?? null
  // null = the live/current plan (getWeeklyPlan, actionable if pending_approval); a weekIso = a past
  // week viewed READ-ONLY from the history rail (GET /weekly-plan?week=…).
  const [selectedWeek, setSelectedWeek] = useState<string | null>(null)

  const { data, isLoading, error } = useQuery<WeeklyPlanResponse>({
    queryKey: [...QUERY_KEY, selectedWeek],
    queryFn: () => apiRequest("GET", selectedWeek ? `/api/dfy/weekly-plan?week=${encodeURIComponent(selectedWeek)}` : "/api/dfy/weekly-plan"),
    // The plan updates asynchronously (generating → pending_approval), so don't serve the global
    // staleTime:Infinity cache: always refetch on mount/focus, and poll while still generating.
    // A historical (terminal) week never changes, so only poll the live view.
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchInterval: (q) => (selectedWeek === null && q.state.data?.cycle?.phase === "generating" ? 15000 : false),
  })

  const { data: weeksData } = useQuery<WeeksResponse>({
    queryKey: WEEKS_KEY,
    queryFn: () => apiRequest("GET", "/api/dfy/weeks"),
    staleTime: 0,
    refetchOnMount: "always",
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY }) // prefix — matches every ?week= variant
    queryClient.invalidateQueries({ queryKey: WEEKS_KEY })
  }

  // A 409/423 means the plan changed underneath us (version skew / handed over) — show the server's
  // reason and refetch.
  const onMutationError = (err: any, fallback: string) => {
    const status = err?.status
    if (status === 409 || status === 423) {
      toast({ title: "Plan wurde aktualisiert", description: err?.data?.error || "Bitte prüfen Sie die neueste Version.", variant: "destructive" })
      setEditing(null)
      refresh()
      return
    }
    toast({ title: fallback, description: err?.data?.error || err?.message || "", variant: "destructive" })
  }

  const approveMutation = useMutation({
    mutationFn: (cycleId: string) => apiRequest("POST", "/api/dfy/weekly-plan/approve", { cycleId }),
    onSuccess: () => {
      refresh()
      toast(data?.publishHeld
        ? { title: "Wochenplan freigegeben", description: "In der Testphase wird nichts veröffentlicht." }
        : { title: "Wochenplan freigegeben", description: "Lina übernimmt jetzt die Veröffentlichung." })
    },
    onError: (err) => onMutationError(err, "Freigabe fehlgeschlagen"),
  })

  const editMutation = useMutation({
    mutationFn: ({ id, title, postContent, version }: { id: string; title: string; postContent: string; version: number }) =>
      apiRequest("PATCH", `/api/dfy/posts/${id}`, { title, postContent, version }),
    onSuccess: () => {
      setEditing(null)
      refresh()
      toast({ title: "Beitrag gespeichert" })
    },
    onError: (err) => onMutationError(err, "Speichern fehlgeschlagen"),
  })

  const swapMutation = useMutation({
    mutationFn: ({ id, uploadId, slide, version }: { id: string; uploadId: string; slide: number; version: number }) =>
      apiRequest("PATCH", `/api/dfy/posts/${id}`, { media: { uploadId, slide }, version }),
    onSuccess: (res: { post?: PlanPost }, vars) => {
      // Keep the dialog (and the text draft) open on the new version, so a following text save does not conflict.
      // Only for the post the swap was for: a late answer must never retarget another post's open editor.
      if (editingIdRef.current === vars.id) {
        setEditing((prev) => (prev && res?.post && prev.id === vars.id ? { ...prev, ...res.post } : prev))
        setPickerSlide(null)
      }
      refresh()
      toast({ title: "Bild getauscht" })
    },
    onError: (err, vars) => {
      if (editingIdRef.current === vars.id) onMutationError(err, "Bild konnte nicht getauscht werden")
      else toast({ title: "Bild konnte nicht getauscht werden", variant: "destructive" })
    },
  })

  const swapAllowed = !!editing && !!data?.imageSwapEnabled && selectedWeek === null
    && effectiveMediaFormat(editing) !== "reel" && swapBlockedReason(editing) === null
  const { data: libraryPhotos, isLoading: libraryLoading } = useQuery<ContentUpload[]>({
    queryKey: ["/api/uploads/my-approved"],
    queryFn: () => apiRequest("GET", "/api/uploads/my-approved"),
    enabled: swapAllowed && pickerSlide !== null,
    staleTime: 0,
  })
  const photos = (libraryPhotos ?? []).filter((u) => (u.mimeType ?? "").startsWith("image/"))

  const deleteMutation = useMutation({
    mutationFn: ({ id, version }: { id: string; version: number }) =>
      apiRequest("DELETE", `/api/dfy/posts/${id}?version=${encodeURIComponent(String(version))}`),
    onSuccess: () => {
      refresh()
      toast({ title: "Beitrag entfernt" })
    },
    onError: (err) => onMutationError(err, "Entfernen fehlgeschlagen"),
  })

  function openEditor(post: PlanPost) {
    setEditing(post)
    setPickerSlide(null)
    setDraftTitle(post.title ?? "")
    setDraftContent(post.postContent ?? "")
  }

  // History rail data (independent query) — the current ISO week comes from the server so the client
  // needn't recompute Berlin weeks.
  const weeks = weeksData?.weeks ?? []
  const currentWk = weeksData?.currentWeekIso ?? ""
  const viewingHistory = selectedWeek !== null
  // The week the LIVE plan is actually on (which getWeeklyPlan chose — may differ from the calendar
  // current week, e.g. a next-week plan awaiting approval, or an unapproved plan lingering past
  // week-end). That row is the actionable one: clicking it returns to the live view (keeps approve),
  // and it's highlighted while selectedWeek === null. "Aktuell" (currentWk) is a separate calendar tag.
  const livePlanWeek = selectedWeek === null ? (data?.cycle?.weekIso ?? null) : null

  let mainContent: ReactNode
  if (isLoading) {
    mainContent = (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Wochenplan wird geladen…
      </div>
    )
  } else if (error || !data || !data.cycle) {
    mainContent = (
      <div className="py-16 text-center text-muted-foreground">
        <CalendarClock className="h-10 w-10 mx-auto mb-4 opacity-60" />
        <h1 className="text-xl font-semibold mb-2">{viewingHistory ? "Diese Woche ist nicht verfügbar" : "Noch kein Wochenplan"}</h1>
        <p>{viewingHistory
          ? "Für diese Woche gibt es keinen Plan zum Anzeigen."
          : "Sobald Lina Ihren nächsten Wochenplan erstellt hat, erscheint er hier zur Freigabe."}</p>
      </div>
    )
  } else {
    const { cycle, posts, allowedActions } = data
    const publishHeld = data.publishHeld === true
    const canEditPost = (p: PlanPost) => p.editability ? p.editability.canEdit : allowedActions.canEdit
    const canDeletePost = (p: PlanPost) => p.editability ? p.editability.canDelete : allowedActions.canDelete
    const phase = PHASE_LABEL[cycle.phase] ?? { label: cycle.phase, className: "text-muted-foreground border-muted" }
    const isTerminalError = cycle.phase === "failed" || cycle.phase === "stalled" || cycle.phase === "empty"
    // Piece B: a freed/published week is shown read-only (getWeeklyPlan's running-week fallback). The
    // banner reassures the user the plan is locked-in & scheduled — not empty or broken.
    const isApprovedReadOnly = cycle.phase === "approved" || cycle.phase === "publishing" || cycle.phase === "published"

    mainContent = (
      <>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Wochenplan</h1>
            <p className="text-muted-foreground">{cycle.weekIso}</p>
          </div>
          <Badge variant="outline" className={`gap-1 ${phase.className}`}>{phase.label}</Badge>
        </div>

        {isTerminalError && (
          <Card className="border-red-200">
            <CardContent className="flex items-start gap-3 py-4 text-sm text-muted-foreground">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <span>Für diese Woche konnte kein Plan erstellt werden. Lina versucht es im nächsten Zyklus erneut.</span>
            </CardContent>
          </Card>
        )}

        {isApprovedReadOnly && (
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="flex items-start gap-3 py-4 text-sm text-muted-foreground">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span>
                {cycle.phase === "published"
                  ? "Freigegeben und für diese Woche eingeplant. Lina übernimmt die Veröffentlichung."
                  : publishHeld
                    ? "Freigegeben. In der Testphase wird nichts veröffentlicht. Sie können Beiträge weiterhin ändern oder entfernen."
                    : "Freigegeben und eingeplant. Beiträge, die noch nicht zur Veröffentlichung übergeben sind, können Sie noch ändern oder entfernen."}
              </span>
            </CardContent>
          </Card>
        )}

        <div className="space-y-3">
          {posts.map((post) => (
            <Card key={post.id} data-testid={`post-${post.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  {isHotel ? (
                    <button
                      type="button"
                      className="flex items-center gap-2 text-left hover:underline"
                      onClick={() => setLocation(postModuleHref(post, edition))}
                      data-testid={`open-module-${post.id}`}
                    >
                      {(() => {
                        const fmt = effectiveMediaFormat(post)
                        const Icon = MEDIA_ICON[fmt]
                        return <Icon className="h-4 w-4 text-muted-foreground flex-shrink-0" aria-label={MEDIA_FORMAT_LABELS[fmt]} />
                      })()}
                      <CardTitle className="text-base">{post.title || "Ohne Titel"}</CardTitle>
                      <ExternalLink className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  ) : (
                    <CardTitle className="text-base">{post.title || "Ohne Titel"}</CardTitle>
                  )}
                  <div className="flex items-center gap-1">
                    {!isHotel && isSignalRoomSourced(post.sourceRef) && (
                      <Badge variant="outline" className="text-xs text-pink-600 border-pink-300" data-testid={`badge-signal-room-${post.id}`}>
                        Signal Room
                      </Badge>
                    )}
                    {post.userEdited && <Badge variant="outline" className="text-xs">bearbeitet</Badge>}
                    {post.publishStatus === "published" && (
                      <Badge variant="outline" className="text-xs gap-1 text-green-600 border-green-300">
                        <CheckCircle className="h-3 w-3" />
                        {post.scheduledFor && new Date(post.scheduledFor).getTime() > Date.now() ? "geplant" : "veröffentlicht"}
                      </Badge>
                    )}
                    {post.publishStatus === "manual" && (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-300" data-testid={`badge-reel-manual-${post.id}`}>
                        Ihr Team veröffentlicht das Reel
                      </Badge>
                    )}
                    {post.publishStatus === "failed" && (
                      <Badge variant="outline" className="text-xs text-red-600 border-red-300">Fehler</Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Reels (B2) show their poster; the video plays on the Instagram-Reels page. */}
                {!post.imageUrl && post.posterUrl && (
                  <img
                    src={post.posterUrl}
                    alt={post.title || "Vorschaubild"}
                    className="rounded-md max-h-72 w-auto object-cover border aspect-[9/16]"
                    loading="lazy"
                  />
                )}
                {hasSlides(post) ? (
                  <PostSlides
                    urls={post.imageUrls}
                    alt={post.title || "Beitragsbild"}
                    className="w-fit max-w-full"
                    imgClassName="rounded-md max-h-72 w-auto object-cover border"
                    testId={`slides-${post.id}`}
                  />
                ) : post.imageUrl && (
                  <img
                    src={post.imageUrl}
                    alt={post.title || "Beitragsbild"}
                    className="rounded-md max-h-72 w-auto object-cover border"
                    loading="lazy"
                  />
                )}
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">{post.postContent}</p>
                {post.scheduledFor && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5" /> Geplant für {formatDateTime(post.scheduledFor)}
                  </p>
                )}
                {!canEditPost(post) && !canDeletePost(post) && post.editability && LOCK_HINT[post.editability.reason] && (
                  <p className="text-xs text-muted-foreground" data-testid={`lock-${post.id}`}>{LOCK_HINT[post.editability.reason]}</p>
                )}
                {(canEditPost(post) || canDeletePost(post)) && (
                  <div className="flex gap-2">
                    {canEditPost(post) && (
                      <Button size="sm" variant="outline" onClick={() => openEditor(post)} data-testid={`edit-${post.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                      </Button>
                    )}
                    {canDeletePost(post) && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="sm" variant="ghost" className="text-red-600" data-testid={`delete-${post.id}`}>
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Entfernen
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Beitrag entfernen?</AlertDialogTitle>
                            <AlertDialogDescription>Dieser Beitrag wird aus dem Wochenplan entfernt und nicht veröffentlicht.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                            <AlertDialogAction onClick={() => deleteMutation.mutate({ id: post.id, version: post.version })}>Entfernen</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {posts.length === 0 && <p className="text-muted-foreground text-sm">Keine Beiträge in diesem Plan.</p>}
        </div>

        {allowedActions.canApprove && (
          <div className="sticky bottom-4 flex justify-end">
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button size="lg" disabled={approveMutation.isPending} data-testid="approve-plan">
                  {approveMutation.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                  Wochenplan freigeben
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Wochenplan freigeben?</AlertDialogTitle>
                  <AlertDialogDescription>
                    {publishHeld
                      ? "In der Testphase wird nichts veröffentlicht. Sie können Beiträge nach der Freigabe weiterhin ändern oder entfernen."
                      : "Nach der Freigabe übernimmt Lina die Veröffentlichung. Sie können Beiträge noch ändern oder entfernen, bis sie zur Veröffentlichung übergeben werden."}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                  <AlertDialogAction onClick={() => approveMutation.mutate(cycle.id)}>Freigeben</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}

        <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Beitrag bearbeiten</DialogTitle>
              <DialogDescription>Änderungen werden beim Veröffentlichen übernommen.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} placeholder="Titel" />
              <Textarea value={draftContent} onChange={(e) => setDraftContent(e.target.value)} rows={10} placeholder="Beitragstext" />
              {swapAllowed && editing && (
                <div className="space-y-2" data-testid="image-swap">
                  <p className="text-sm font-medium">{postPictures(editing).length > 1 ? "Bilder" : "Bild"}</p>
                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {postPictures(editing).map((url, i) => (
                      <button
                        key={`${i}-${url}`}
                        type="button"
                        onClick={() => setPickerSlide(pickerSlide === i + 1 ? null : i + 1)}
                        className={cn("relative shrink-0 overflow-hidden rounded-md border", pickerSlide === i + 1 && "ring-2 ring-primary")}
                        aria-label={`Bild ${i + 1} tauschen`}
                        data-testid={`swap-slide-${i + 1}`}
                      >
                        <img src={url} alt="" className="h-24 w-auto aspect-[4/5] object-cover" loading="lazy" />
                        {postPictures(editing).length > 1 && (
                          <span className="absolute bottom-1 right-1 rounded-full bg-black/60 px-1.5 text-xs text-white">{i + 1}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Tippen Sie auf ein Bild und wählen Sie ein Foto aus Ihrer Bibliothek. Das Foto wird bei Bedarf mittig auf das passende Format zugeschnitten.
                    {isSignalRoomSourced(editing.sourceRef) && " Text und Gestaltung der Folie bleiben erhalten."}
                  </p>
                  {pickerSlide !== null && (
                    <div className="rounded-md border p-2" data-testid="swap-picker">
                      {libraryLoading ? (
                        <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Fotos werden geladen…
                        </div>
                      ) : photos.length === 0 ? (
                        <p className="py-4 text-center text-sm text-muted-foreground">In Ihrer Foto-Bibliothek sind noch keine Fotos.</p>
                      ) : (
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                          {photos.map((photo) => (
                            <button
                              key={photo.id}
                              type="button"
                              disabled={swapMutation.isPending || editMutation.isPending}
                              onClick={() => swapMutation.mutate({ id: editing.id, uploadId: photo.id, slide: pickerSlide, version: editing.version })}
                              className="overflow-hidden rounded-md border hover:ring-2 hover:ring-primary disabled:opacity-50"
                              aria-label={`Foto ${photo.originalName || ""} übernehmen`}
                              data-testid={`swap-photo-${photo.id}`}
                            >
                              <img src={photo.filePath} alt="" className="w-full aspect-[4/5] object-cover" loading="lazy" />
                            </button>
                          ))}
                        </div>
                      )}
                      {swapMutation.isPending && (
                        <div className="flex items-center gap-2 pt-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" /> Bild wird übernommen…
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Abbrechen</Button>
              <Button
                disabled={editMutation.isPending || swapMutation.isPending}
                onClick={() => editing && editMutation.mutate({ id: editing.id, title: draftTitle, postContent: draftContent, version: editing.version })}
              >
                {editMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Speichern
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {viewingHistory && (
        <button
          type="button"
          onClick={() => setSelectedWeek(null)}
          className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          data-testid="back-to-current"
        >
          <ArrowLeft className="h-4 w-4" /> Aktuelle Woche
        </button>
      )}
      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex-1 min-w-0 space-y-6">{mainContent}</div>

        {weeks.length > 0 && (
          <aside className="lg:w-64 lg:shrink-0" data-testid="week-history">
            <div className="lg:sticky lg:top-6">
              <h2 className="px-1 mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Verlauf</h2>
              <div className="space-y-1">
                {weeks.map((w) => {
                  const isCurrent = w.weekIso === currentWk // calendar tag only ("Aktuell")
                  const isLive = w.weekIso === livePlanWeek // the actionable live-plan row
                  const isSelected = selectedWeek === w.weekIso || isLive
                  const lbl = PHASE_LABEL[w.phase] ?? { label: w.phase, className: "text-muted-foreground border-muted" }
                  return (
                    <button
                      key={w.weekIso}
                      type="button"
                      onClick={() => setSelectedWeek(isLive ? null : w.weekIso)}
                      data-testid={`week-${w.weekIso}`}
                      aria-current={isSelected ? "page" : undefined}
                      className={cn(
                        "w-full rounded-md border px-3 py-2 text-left transition-colors",
                        isSelected ? "border-primary/40 bg-muted" : "border-transparent hover:bg-muted/60",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{w.weekIso}</span>
                        {isCurrent && <Badge variant="outline" className="px-1 py-0 text-[10px]">Aktuell</Badge>}
                      </div>
                      <Badge variant="outline" className={cn("mt-1 text-[11px]", lbl.className)}>{lbl.label}</Badge>
                    </button>
                  )
                })}
              </div>
            </div>
          </aside>
        )}
      </div>
    </div>
  )
}
